import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import {
  Building2,
  Package,
  BarChart3,
  MessageSquare,
  ArrowRight,
  Activity,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import { EmptyTenantState } from '@/components/EmptyTenantState';
import { PageHeader } from '@/components/PageHeader';
import { SkeletonStats } from '@/components/SkeletonStats';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/')({
  head: () => ({
    meta: [{ title: 'Dashboard · Replya Internal Backoffice' }],
  }),
  component: DashboardPage,
});

/** Nome leggibile della sorgente dell'indice mostrata nelle stat card. */
function indexLabel(source: 'mongo' | 'qdrant') {
  return source === 'qdrant' ? 'Qdrant' : 'MongoDB';
}

/** Data in formato YYYY-MM-DD di `days` giorni fa, usata per filtrare i log. */
function isoDaysAgo(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function DashboardPage() {
  const { selectedTenant } = useAppState();
  const [errorsOpen, setErrorsOpen] = useState(false);
  const errorsStartDate = isoDaysAgo(7);

  if (!selectedTenant) {
    return <EmptyTenantState />;
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader
        title="Dashboard"
        subtitle={`Panoramica operativa per ${selectedTenant.name}`}
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <StatCard
          label="Prodotti"
          icon={Package}
          queryKey={['tenantStats', selectedTenant.tenantId]}
          queryFn={() => apiService.getTenantStats(selectedTenant.tenantId)}
          format={(data) => String(data.totalProducts)}
          detail={(data) =>
            data.indexAvailable
              ? `${data.indexedProducts} indicizzati su ${indexLabel(data.indexSource)}`
              : `indice ${indexLabel(data.indexSource)} non raggiungibile`
          }
        />
        <StatCard
          label="Copertura indice"
          icon={Activity}
          queryKey={['tenantStats', selectedTenant.tenantId]}
          queryFn={() => apiService.getTenantStats(selectedTenant.tenantId)}
          format={(data) => (data.indexAvailable ? `${Math.round(data.indexCoverage)}%` : 'n/d')}
          detail={(data) =>
            data.indexSource === 'qdrant'
              ? `motore ${data.engineVersion} · Qdrant${
                  data.documentChunks !== null ? ` · ${data.documentChunks} chunk documenti` : ''
                }`
              : `motore ${data.engineVersion} · embedding su MongoDB`
          }
        />
        <StatCard
          label="Richieste 7gg"
          icon={BarChart3}
          queryKey={['statsSummary', selectedTenant.tenantId, 7]}
          queryFn={() => apiService.getStatsSummary(selectedTenant.tenantId, 7)}
          format={(data) => String(data.totalRequests)}
        />
        <StatCard
          label="Conversazioni 7gg"
          icon={MessageSquare}
          queryKey={['statsSummary', selectedTenant.tenantId, 7]}
          queryFn={() => apiService.getStatsSummary(selectedTenant.tenantId, 7)}
          format={(data) => String(data.totalConversations)}
        />
        <StatCard
          label="Errori 7gg"
          icon={AlertTriangle}
          queryKey={['statsSummary', selectedTenant.tenantId, 7]}
          queryFn={() => apiService.getStatsSummary(selectedTenant.tenantId, 7)}
          format={(data) => String(data.totalErrors)}
          detail={(data) => (data.totalErrors > 0 ? 'Clicca per vedere il dettaglio' : 'Nessun errore registrato')}
          variant="destructive"
          onClick={() => setErrorsOpen(true)}
        />
        <StatCard
          label="Tempo medio risposta chatbot"
          icon={Clock}
          queryKey={['statsSummary', selectedTenant.tenantId, 7]}
          queryFn={() => apiService.getStatsSummary(selectedTenant.tenantId, 7)}
          format={(data) =>
            data.averageChatbotResponseTime ? `${data.averageChatbotResponseTime}ms` : 'n/d'
          }
          detail={(data) => `${data.averageResponseTime}ms su tutte le richieste HTTP`}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="rounded-2xl border bg-surface">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4 text-brand" />
              Tenant attivo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <span className="text-muted-foreground">Nome:</span>{' '}
              <span className="font-medium text-foreground">{selectedTenant.name}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Database:</span>{' '}
              <span className="font-medium text-foreground">{selectedTenant.dbName}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Creato il:</span>{' '}
              <span className="font-medium text-foreground">
                {new Date(selectedTenant.createdAt).toLocaleDateString()}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border bg-surface">
          <CardHeader>
            <CardTitle className="text-base">Collegamenti rapidi</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2">
            <QuickLink to="/analytics" label="Analytics" />
            <QuickLink to="/products" label="Catalogo prodotti" />
            <QuickLink to="/chat-test" label="Chat Test" />
            <QuickLink to="/battery-test" label="Batterie Test" />
            <QuickLink to="/logs" label="Log operativi" />
          </CardContent>
        </Card>
      </div>

      <ErrorsDetailDialog
        tenantId={selectedTenant.tenantId}
        open={errorsOpen}
        onOpenChange={setErrorsOpen}
        startDate={errorsStartDate}
      />
    </div>
  );
}

/**
 * Dettaglio degli errori conteggiati dalla card "Errori 7gg": legge i log
 * server (`/logs/errors`) sullo stesso intervallo del contatore.
 */
function ErrorsDetailDialog({
  tenantId,
  open,
  onOpenChange,
  startDate,
}: {
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startDate: string;
}) {
  const summaryQuery = useQuery({
    queryKey: ['statsSummary', tenantId, 7],
    queryFn: () => apiService.getStatsSummary(tenantId, 7),
    enabled: open,
  });

  // Il contatore usa come cutoff la mezzanotte locale di 7 giorni fa: la stessa
  // soglia va passata al dettaglio, altrimenti i due numeri non coincidono.
  const startIso = new Date(`${startDate}T00:00:00`).toISOString();

  const logsQuery = useQuery({
    queryKey: ['errorLogs', tenantId, 'dashboard-7d', startIso],
    queryFn: () => apiService.getErrorLogs(tenantId, { limit: 25, startDate: startIso }),
    enabled: open,
  });

  const logs = logsQuery.data?.items ?? [];
  const total = logsQuery.data?.total ?? 0;
  const counted = summaryQuery.data?.totalErrors ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl border bg-surface max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Errori ultimi 7 giorni
          </DialogTitle>
          <DialogDescription>
            Errori registrati dal {new Date(startDate).toLocaleDateString('it-IT')} a oggi.
          </DialogDescription>
        </DialogHeader>

        {logsQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Caricamento...</p>
        ) : logsQuery.isError ? (
          <p className="text-sm text-destructive">
            Impossibile leggere i log errori: {(logsQuery.error as Error).message}
          </p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {counted > 0
              ? `Il contatore riporta ${counted} errori nel periodo ma nessun dettaglio è presente su /logs/errors: gli errori non vengono loggati singolarmente dal backend.`
              : 'Nessun errore registrato nel periodo selezionato.'}
          </p>
        ) : (
          <div className="overflow-x-auto -mx-2 px-2">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Origine</TableHead>
                  <TableHead>Messaggio</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums">
                      {log.createdAt ? new Date(log.createdAt).toLocaleString('it-IT') : '—'}
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-[180px] truncate" title={log.path || log.source}>
                      {log.path
                        ? `${log.method ? `${log.method} ` : ''}${log.path}`
                        : log.source || '—'}
                    </TableCell>
                    <TableCell className="text-xs max-w-[320px]">
                      <p className="whitespace-pre-wrap break-words">{log.message || '—'}</p>
                      {log.stack && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-muted-foreground">Stack trace</summary>
                          <pre className="mt-1 max-h-48 overflow-auto rounded-xl bg-surface-3/60 p-2 text-[11px] leading-relaxed">
                            {log.stack}
                          </pre>
                        </details>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.statusCode ? (
                        <Badge variant="destructive" className="text-xs">
                          {log.statusCode}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
          <span>
            {logs.length > 0 ? `${logs.length} di ${total} errori mostrati` : ''}
          </span>
          <Link
            to="/logs"
            search={{ tab: 'errors', startDate }}
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
          >
            Apri nei log operativi
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QuickLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-medium text-foreground hover:bg-surface-2 transition-colors"
    >
      {label}
      <ArrowRight className="h-4 w-4 text-muted-foreground" />
    </Link>
  );
}

interface StatCardProps<T> {
  label: string;
  icon: React.ElementType;
  queryKey: QueryKey;
  queryFn: () => Promise<T>;
  format: (data: T) => string | number;
  detail?: (data: T) => string;
  variant?: 'default' | 'destructive';
  onClick?: () => void;
}

function StatCard<T>({
  label,
  icon: Icon,
  queryKey,
  queryFn,
  format,
  detail,
  variant = 'default',
  onClick,
}: StatCardProps<T>) {
  const { data, isLoading } = useQuery({ queryKey, queryFn });

  if (isLoading) {
    return (
      <Card className="rounded-2xl border bg-surface">
        <CardContent className="p-5 space-y-3">
          <SkeletonStats count={1} />
        </CardContent>
      </Card>
    );
  }

  const card = (
    <Card
      className={`rounded-2xl border bg-surface${
        onClick ? ' cursor-pointer transition-colors hover:bg-surface-2' : ''
      }`}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <Icon
            className={`h-4 w-4 ${
              variant === 'destructive' ? 'text-destructive' : 'text-brand'
            }`}
          />
        </div>
        <p className={`mt-2 text-3xl font-bold tracking-tight ${variant === 'destructive' ? 'text-destructive' : 'text-foreground'}`}>
          {data ? format(data) : '-'}
        </p>
        {data && detail && <p className="mt-1 text-xs text-muted-foreground">{detail(data)}</p>}
      </CardContent>
    </Card>
  );

  if (!onClick) return card;

  return (
    <button type="button" onClick={onClick} className="text-left" aria-label={`${label}: apri dettaglio`}>
      {card}
    </button>
  );
}
